import { useId, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  useAddCmpNewsletterSubscriberMutation,
  useGetCmpNewsletterSubscribersQuery,
  useRemoveCmpNewsletterSubscriberMutation,
} from "../../../graphql/types";
import {
  DataTable,
  dataTableLoadingRowContent,
} from "../../common/dashboard/DataTable";
import { ModalShell } from "../../common/dashboard/ModalShell";
import { ConfirmDialog } from "../../common/dashboard/ConfirmDialog";
import { ActionSuccessDialog } from "../../common/dashboard/ActionSuccessDialog";
import { EMAIL_OPERATION_ERROR_CONTEXT, emailLoadErrorText } from "./emailBackendMappers";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../utils/userFacingError";
import {
  EmailDesignScope,
  inputClassName,
  primaryButtonClassName,
} from "./emailWizardShared";

type Props = {
  chamberId: string;
  onClose: () => void;
  onChanged: () => void;
};

const ManageSubscriberListModal = ({
  chamberId,
  onClose,
  onChanged,
}: Props) => {
  const titleId = useId();
  const [search, setSearch] = useState("");
  const [entry, setEntry] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Completed changes use the design success pop-up. Outcomes that did not
  // change the list (already active, suppressed, not found) stay inline.
  const [successMessage, setSuccessMessage] = useState("");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");
  const [removeEmail, setRemoveEmail] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  const { data, loading, error, refetch } = useGetCmpNewsletterSubscribersQuery(
    {
      variables: { input: { chamberId, includeUnsubscribed: false } },
      skip: !chamberId,
      fetchPolicy: "network-only",
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    }
  );
  const [addSubscriber, { loading: adding }] =
    useAddCmpNewsletterSubscriberMutation({
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    });
  const [removeSubscriber, { loading: removing }] =
    useRemoveCmpNewsletterSubscriberMutation({
      context: EMAIL_OPERATION_ERROR_CONTEXT,
    });
  const busy = adding || removing;
  const rows = (data?.getCmpNewsletterSubscribers.subscribers || []).filter(
    (row) =>
      `${row.name} ${row.email}`
        .toLowerCase()
        .includes(search.trim().toLowerCase())
  );
  const refreshAfterChange = async (changeSaved = true) => {
    if (changeSaved) onChanged();
    try {
      await refetch();
    } catch {
      setActionError(
        `${changeSaved ? "The change was saved. " : ""}The subscriber list could not be refreshed. Close and open the list to try again.`
      );
    }
  };
  const requestClose = () => {
    if (busy) return;
    if (Object.values(entry).some((value) => value.trim()))
      setDiscardOpen(true);
    else onClose();
  };
  const add = async () => {
    const nextErrors: Record<string, string> = {};
    if (!entry.firstName.trim())
      nextErrors.firstName = "First Name is required";
    if (!entry.lastName.trim()) nextErrors.lastName = "Last Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry.email.trim()))
      nextErrors.email = "Enter a valid email address.";
    setErrors(nextErrors);
    setActionError("");
    setNotice("");
    setSuccessMessage("");
    if (Object.keys(nextErrors).length || !chamberId || busy) return;
    try {
      const result = await addSubscriber({
        variables: {
          input: {
            chamberId,
            email: entry.email.trim(),
            firstName: entry.firstName.trim(),
            lastName: entry.lastName.trim(),
          },
        },
      });
      const outcome = result.data?.addCmpNewsletterSubscriber;
      if (!outcome) throw new Error("The subscriber could not be added.");
      if (outcome.outcome === "CREATED") {
        setEntry({ firstName: "", lastName: "", email: "" });
        setSuccessMessage(outcome.message);
        await refreshAfterChange();
      } else {
        setNotice(outcome.message);
      }
    } catch (error) {
      setActionError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(`add ${entry.email.trim()} to the subscriber list`)
        )
      );
    }
  };
  const remove = async () => {
    if (!removeEmail || busy) return;
    setActionError("");
    setNotice("");
    setSuccessMessage("");
    try {
      const result = await removeSubscriber({
        variables: { input: { chamberId, email: removeEmail } },
      });
      const outcome = result.data?.removeCmpNewsletterSubscriber;
      if (
        !outcome ||
        !["UNSUBSCRIBED", "NOT_FOUND"].includes(outcome.outcome)
      )
        throw new Error("The subscriber could not be removed.");
      if (outcome.outcome === "UNSUBSCRIBED") setSuccessMessage(outcome.message);
      else setNotice(outcome.message);
      setRemoveEmail("");
      await refreshAfterChange(outcome.outcome === "UNSUBSCRIBED");
    } catch (error) {
      setActionError(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback(`remove ${removeEmail} from the subscriber list`)
        )
      );
      setRemoveEmail("");
    }
  };
  return (
    <>
      <ModalShell
        open
        enableKeyboardFocus
        onClose={requestClose}
        labelledById={titleId}
        ariaBusy={busy}
        showCloseButton={false}
        panelClassName="relative max-w-[700px] !max-h-[92vh] !rounded-[14px] !border-[#ECECEC] !p-0"
      >
        <EmailDesignScope>
          <div className="flex max-h-[calc(92vh-2px)] flex-col">
          <div className="relative shrink-0 border-b border-[#ECECEC] px-5 pb-4 pt-6 md:px-8">
            <button
              type="button"
              aria-label="Close subscriber list"
              disabled={busy}
              onClick={requestClose}
              className="absolute right-[14px] top-[14px] flex h-7 w-7 items-center justify-center rounded-full bg-[#F21B1B] text-white"
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </button>
            <h2 id={titleId} className="pr-7 text-[18px] font-bold">
              Add / Edit Subscriber List
            </h2>
            <p className="mt-1 text-[14px] leading-[1.5]">
              This list is pulled from the member directory, all email
              subscribers from the website, and any manual entries. Add an email
              to the subscriber list or delete an email from the subscriber list
              only. Member emails must be updated in Manage Members
            </p>
          </div>
          <div className="flex min-h-0 flex-col gap-[18px] overflow-y-auto px-5 py-6 md:px-8">
          <div className="flex flex-col gap-3">
            <h3 className="text-[16px] font-semibold">
              Add an Email
            </h3>
            <div className="grid grid-cols-1 gap-x-5 gap-y-[14px] md:grid-cols-2">
              {(
                [
                  { key: "firstName", label: "First Name" },
                  { key: "lastName", label: "Last Name" },
                  { key: "email", label: "Email Address" },
                ] as const
              ).map(({ key, label }) => (
                <label key={key} className="text-[14px] font-bold">
                  <span>
                    {label} <span className="text-[#F0202E]">*</span>
                  </span>
                  <input
                    className={`${inputClassName} mt-1.5 !font-normal ${errors[key] ? "!border-[#E24C4C]" : ""}`}
                    type={key === "email" ? "email" : "text"}
                    disabled={busy}
                    placeholder={`Enter ${label}`}
                    value={entry[key]}
                    maxLength={key === "email" ? 254 : 100}
                    aria-invalid={!!errors[key]}
                    onChange={(event) =>
                      setEntry({ ...entry, [key]: event.target.value })
                    }
                  />
                  {errors[key] && (
                    <span
                      role="alert"
                      className="mt-1.5 block text-[12px] font-normal text-[#E24C4C]"
                    >
                      {errors[key]}
                    </span>
                  )}
                </label>
              ))}
            </div>
            <button
              type="button"
              className={`${primaryButtonClassName} w-fit`}
              disabled={busy}
              onClick={add}
            >
              {adding ? "Adding..." : "+\u00a0\u00a0Add Email"}
            </button>
            {notice && (
              <p role="status" className="text-sm">
                {notice}
              </p>
            )}
            {actionError && (
              <p role="alert" className="text-sm text-red-600">
                {actionError}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-[16px] font-semibold">
              Subscriber List
            </h3>
            <div className="relative">
              <SearchIcon aria-hidden="true" sx={{ fontSize: 15 }} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9E9E]" />
              <input
                aria-label="Search subscriber list"
                className={`${inputClassName} !pl-9 !font-normal`}
                placeholder="Search by name or email"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            {error ? (
              <p role="alert" className="text-sm text-red-600">
                {emailLoadErrorText("the subscriber list")}{" "}
                <button
                  type="button"
                  className="underline"
                  onClick={() => void refreshAfterChange(false)}
                >
                  Try again
                </button>
              </p>
            ) : (
              <DataTable
                ariaLabel="Subscriber list"
                keepFeedbackInViewport
                containerClassName="max-h-[322px] w-full overflow-x-auto overflow-y-auto rounded-[10px] border border-[#ECECEC] bg-white"
                rows={rows}
                getRowKey={(row) => row.id}
                showLoadingRow={loading && !data}
                loadingRowContent={dataTableLoadingRowContent(
                  "Loading subscribers..."
                )}
                showEmptyRow={!loading && rows.length === 0}
                emptyRowContent={
                  search
                    ? "No subscribers match your search."
                    : "No subscribers yet."
                }
                tableSx={{
                  minWidth: 440,
                  "& .MuiTableCell-root": {
                    padding: "11px 14px",
                    fontSize: 14,
                    borderColor: "#F0F0F0",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    backgroundColor: "#F7F7F8",
                    borderColor: "#ECECEC",
                  },
                }}
                columns={[
                  {
                    key: "name",
                    header: "NAME",
                    render: (row) => row.name || "Not provided",
                  },
                  {
                    key: "email",
                    header: "EMAIL ADDRESS",
                    render: (row) => row.email,
                  },
                  {
                    key: "source",
                    header: "SOURCE",
                    render: (row) => (
                      <span className="text-[#727272]">
                        {row.isMemberEmail ? "Member Directory" : row.source}
                      </span>
                    ),
                  },
                  {
                    key: "actions",
                    header: <span className="sr-only">Actions</span>,
                    width: "52px",
                    render: (row) => (
                      <button
                        type="button"
                        title={
                          row.isMemberEmail
                            ? "Member emails must be updated in Manage Members"
                            : "Delete email from subscriber list"
                        }
                        aria-label={
                          row.isMemberEmail
                            ? "Member emails must be updated in Manage Members"
                            : `Delete ${row.email} from subscriber list`
                        }
                        disabled={busy || row.isMemberEmail}
                        className="flex h-8 w-8 items-center justify-center text-[#F21B1B] disabled:text-[#9E9E9E]"
                        onClick={() => setRemoveEmail(row.email)}
                      >
                        {row.isMemberEmail ? (
                          <LockOutlinedIcon
                            sx={{ fontSize: 16, color: "#C4C7CC" }}
                          />
                        ) : (
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        )}
                      </button>
                    ),
                  },
                ]}
              />
            )}
          </div>
          </div>
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-[#ECECEC] bg-[#F7F7F8] px-5 py-[14px] md:px-8">
              {data && !error && (
                <p className="text-[14px] text-[#0A0A0A]">
                  {data.getCmpNewsletterSubscribers.totalCount.toLocaleString(
                    "en-US"
                  )}{" "}
                  subscribers
                </p>
              )}
              <button
                type="button"
                className={`${primaryButtonClassName} ml-auto`}
                disabled={busy}
                onClick={requestClose}
              >
                Done
              </button>
          </div>
          </div>
        </EmailDesignScope>
      </ModalShell>
      <ConfirmDialog
        open={!!removeEmail}
        constrainContentWidth
        variant="confirm"
        title="Remove subscriber?"
        message={`Remove ${removeEmail} from the subscriber list? This address will be unsubscribed.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        loading={removing}
        zIndex={10000}
        onConfirm={remove}
        onClose={() => {
          if (!removing) setRemoveEmail("");
        }}
      />
      <ActionSuccessDialog
        message={successMessage}
        onClose={() => setSuccessMessage("")}
      />
      <ConfirmDialog
        open={discardOpen}
        constrainContentWidth
        variant="confirm"
        title="Discard subscriber entry?"
        message="The email entry has not been added."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        zIndex={10000}
        onConfirm={onClose}
        onClose={() => setDiscardOpen(false)}
      />
    </>
  );
};

export default ManageSubscriberListModal;
